import {
  Camera, CameraDirection, CameraResultType, CameraSource, ImageOptions, Photo,
} from '@capacitor/camera';
import {
  Component, EventEmitter, Input, Output, inject,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

// eslint-disable-next-line no-shadow
import { File } from '@common/entities';

import { ActionSheetButtonStyle } from '@capacitor/action-sheet/dist/esm/definitions';
import { ActionSheetService, ProtoActionSheetButton } from 'src/app/@core/action-sheet.service';
import { DialogService } from 'src/app/@core/dialog.service';
import { FileUploadService } from 'src/app/@core/file-upload.service';
import { Platform } from '@ionic/angular';
import { ProtoImageType } from 'src/app/@shared/proto-image/proto-image.config';
import { ToastService } from 'src/app/@core/toast.service';

const IMAGE_UPLOAD_QUALITY: number = 80;

type ActionSheetSelection = 'album' | 'camera' | 'delete' | 'cancel';

@Component({
  selector: 'app-image-upload',
  standalone: false,
  styleUrls: ['./image-upload.component.scss'],
  templateUrl: './image-upload.component.html',
})
export class ImageUploadComponent {
  @Input() public imageId: string;
  @Input() public imageUrl: string;
  @Input() public showPlaceholderImage: boolean;
  @Input() public imageType: ProtoImageType = 'avatar_large';
  @Input() public imageUploadWidth: number; // optional
  @Input() public imageUploadHeight: number; // optional
  @Input() public imageIdRequired: boolean = true;

  @Output() public fileUpdated: EventEmitter<File> = new EventEmitter<File>();
  @Output() public fileDeleted: EventEmitter<void> = new EventEmitter<void>();

  public imageIsLoading: boolean = false;
  public isWebApp: boolean;

  private uploadedFile: File;

  private actionSheetService = inject(ActionSheetService);
  private dialogService = inject(DialogService);
  private fileUploadService = inject(FileUploadService);
  private platform = inject(Platform);
  private toastService = inject(ToastService);
  private translate = inject(TranslateService);

  public ngOnInit(): void {
    this.isWebApp = this.platform.is('pwa');
  }

  public async showActionsheet(): Promise<void> {
    const options = this.getActionsheetOptions();
    const selectedIndex: number = await this.actionSheetService.show({
      options,
    });

    const selectedId = options[selectedIndex]
      ? options[selectedIndex].id
      : 'cancel';

    let action: ActionSheetSelection;

    switch (selectedId) {
      case 'select':
        action = 'album';
        break;

      case 'take':
        action = 'camera';
        break;

      case 'delete':
        action = 'delete';
        break;

      default:
        action = 'cancel';
    }

    return this.onActionSelected(action);
  }

  public onUploadedImageLoaded(): void {
    this.imageIsLoading = false;
  }

  private async onActionSelected(action: ActionSheetSelection): Promise<void> {
    if (action === 'cancel') {
      return Promise.resolve();
    }

    try {
      if (action === 'delete') {
        const imageDeleted = await this.deleteImage();

        if (imageDeleted) {
          await this.toastService.show(this.translate.instant('VIEW.COMMON.TOAST.IMAGE_DELETE_SUCCESS.MESSAGE'));
          this.fileDeleted.emit();
        }

        return Promise.resolve();
      }

      if (action === 'album' || action === 'camera') {
        const cameraSource = action === 'album'
          ? CameraSource.Photos
          : CameraSource.Camera;
        const imageUpdated = await this.uploadImage(cameraSource);

        if (imageUpdated) {
          this.fileUpdated.emit(this.uploadedFile);
        }
      }
    } catch (error) {
      console.error(error);
      // do not show error toast when user cancelled photos app / hasn't picked any image
      if (typeof error === 'object'
        && error.message !== 'User cancelled photos app'
        && error.message !== 'No image picked') {
        await this.toastService.show(this.translate.instant('VIEW.COMMON.TOAST.GENERIC_ERROR.MESSAGE'), {
          color: 'danger',
        });
      }
    }

    this.imageIsLoading = false;

    return Promise.resolve();
  }

  private async uploadImage(cameraSource: CameraSource.Camera | CameraSource.Photos): Promise<boolean> {
    const options: ImageOptions = {
      allowEditing: false,
      // disable editing until it works properly (see https://github.com/ionic-team/capacitor/issues/2994)
      direction: CameraDirection.Front,

      quality: IMAGE_UPLOAD_QUALITY,
      resultType: CameraResultType.Uri,
      source: cameraSource,
    };

    if (this.imageUploadWidth) {
      options.width = this.imageUploadWidth;
    }

    if (this.imageUploadHeight) {
      options.height = this.imageUploadHeight;
    }

    const image: Photo = await Camera.getPhoto(options);
    const file = await fetch(image.webPath);
    const blobData: Blob = await file.blob();

    this.imageIsLoading = true;
    this.uploadedFile = await this.fileUploadService.upload(blobData, image.format);

    return Promise.resolve(true);
  }

  private async deleteImage(): Promise<boolean> {
    const confirmed = await this.dialogService.showConfirm({
      message: this.translate.instant('VIEW.COMMON.ALERT.CONFIRM_IMAGE_DELETE.MESSAGE'),
      okButtonTitle: this.translate.instant('BUTTON.YES'),
      title: this.translate.instant('VIEW.COMMON.ALERT.CONFIRM_IMAGE_DELETE.TITLE'),
    });

    if (!confirmed) {
      return Promise.resolve(false);
    }

    this.imageIsLoading = true;
    await this.fileUploadService.delete(this.imageId);
    this.imageIsLoading = false;

    return Promise.resolve(true);
  }

  private getActionsheetOptions(): ProtoActionSheetButton[] {
    const options: ProtoActionSheetButton[] = [
      {
        id: 'select',
        title: this.translate.instant('BUTTON.PICTURE_SELECT'),
      },
    ];

    if (!this.isWebApp) {
      options.push({
        id: 'take',
        title: this.translate.instant('BUTTON.PICTURE_TAKE'),
      });
    }

    options.push({
      style: ActionSheetButtonStyle.Cancel,
      title: this.translate.instant('BUTTON.CANCEL'),
    });

    if (this.imageUrl && !this.imageIdRequired) {
      options.push({
        id: 'delete',
        style: ActionSheetButtonStyle.Destructive,
        title: this.translate.instant('BUTTON.PICTURE_DELETE'),
      });
    }

    return options;
  }
}
